import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Button, Card, FormGroup, PageTitle, TextArea } from 'src/components';
import Axios from 'src/services/api';
import styles from './Profile.module.scss';

const TrackingPixels: React.FC = () => {
  const [facebookPixel, setFacebookPixel] = useState('');
  const [googlePixel, setGooglePixel] = useState('');
  const [errors, setErrors] = useState<{ facebookPixel?: string, googlePixel?: string }>({});

  const me = useMemo(
    () => localStorage.getItem("me") && JSON.parse(localStorage.getItem("me")!),
    [localStorage.getItem("me")]
  );

  useEffect(() => {
    if (me?.facebookPixel) {
      setFacebookPixel(me.facebookPixel);
    }
    if (me?.googlePixel) {
      setGooglePixel(me.googlePixel);
    }
  }, [me]);

  const validateFacebookPixel = (value: string) => {
    const trimmedValue = value.trim();

    // Facebook Pixel ID should be a numeric string
    if (!/^\d+$/.test(trimmedValue)) {
      setErrors((prev) => ({
        ...prev,
        facebookPixel: 'Facebook Pixel ID must be a numeric value'
      }));
      return false;
    }

    // Facebook Pixel IDs are typically 15-16 digits long
    if (trimmedValue.length < 15 || trimmedValue.length > 16) {
      setErrors((prev) => ({
        ...prev,
        facebookPixel: 'Facebook Pixel ID should be 15-16 digits long'
      }));
      return false;
    }

    // Clear errors if validation passes
    setErrors((prev) => ({
      ...prev,
      facebookPixel: undefined
    }));

    return true;
  };

  const validateGooglePixel = (value: string) => {
    const trimmedValue = value.trim();

    // Skip validation if empty
    if (!trimmedValue) {
      setErrors({
        ...errors,
        googlePixel: undefined
      });
      return true;
    }

    // Google Analytics ID format validation
    // UA-XXXXXX-Y (Universal Analytics)
    // G-XXXXXXXX (Google Analytics 4)
    const validFormat = /^UA-\d+-\d+$/.test(trimmedValue) || /^G-[A-Z0-9]+$/.test(trimmedValue);

    if (!validFormat) {
      setErrors((prev) => ({
        ...prev,
        googlePixel: 'Google ID should be in format UA-XXXXXX-Y or G-XXXXXXXX'
      }));
      return false;
    }

    // Clear errors if validation passes
    setErrors((prev) => ({
      ...prev,
      googlePixel: undefined
    }));

    return true;
  };

  const handleSave = async () => {
    let isValid = true;

    if (facebookPixel.trim() && !validateFacebookPixel(facebookPixel)) {
      isValid = false;
      setErrors((prev) => ({
        ...prev,
        facebookPixel: 'Facebook Pixel ID is invalid'
      }));
    }

    if (googlePixel.trim() && !validateGooglePixel(googlePixel)) {
      isValid = false;
      setErrors((prev) => ({
        ...prev,
        googlePixel: 'Google Analytics ID is invalid'
      }));
    }

    if (!isValid) return;

    toast.promise(
      Axios.post('/users/update', {
        facebookPixel,
        googlePixel,
      }),
      {
        loading: 'Saving...',
        success: 'Tracking pixels saved successfully',
        error: 'Failed to save tracking pixels',
      }
    );

  };

  const isSaveDisabled = !facebookPixel.trim() && !googlePixel.trim();

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div className={styles.settingsPage}>
        <PageTitle
          actions={
            <Button
              text="Save"
              type="submit"
              disabled={isSaveDisabled}
            />
          }
        >
          Tracking Pixels
        </PageTitle>
        <Card>
          <div className={styles.formsContainer}>
            <FormGroup
              label="Facebook Pixel ID"
              error={errors.facebookPixel}
            >
              <TextArea
                name="facebookPixel"
                value={facebookPixel}
                onChange={(e) => setFacebookPixel(e.target.value)}
                placeholder="Enter your Facebook Pixel ID"
                error={errors.facebookPixel}
              />
            </FormGroup>
            {/* <FormGroup
              label="Google Analytics ID"
              error={errors.googlePixel}
            >
              <TextArea
                name="googlePixel"
                value={googlePixel}
                onChange={(e) => setGooglePixel(e.target.value)}
                placeholder="Enter your Google Analytics ID (UA-XXXXXX-Y or G-XXXXXXXX)"
                error={errors.googlePixel}
              />
            </FormGroup> */}
          </div>
        </Card>
      </div>
    </form>
  );
};

export default TrackingPixels; 